import React, { useState, useMemo } from "react";
import { LuFilter } from "react-icons/lu";
import { useDashboardTooltip } from "./DashboardTooltip";
import { formatCompact } from "./KpiCards";

const FUNNEL_GRADIENTS = [
  { start: "#2563eb", end: "#3b82f6" }, // Royal Blue
  { start: "#0284c7", end: "#38bdf8" }, // Sky Blue
  { start: "#0d9488", end: "#14b8a6" }, // Teal
  { start: "#059669", end: "#10b981" }, // Emerald
  { start: "#d97706", end: "#f59e0b" }, // Amber
  { start: "#ea580c", end: "#f97316" }, // Coral
  { start: "#e11d48", end: "#fb7185" }, // Rose
  { start: "#8b5cf6", end: "#a78bfa" }, // Violet
  { start: "#6366f1", end: "#818cf8" }, // Indigo
  { start: "#475569", end: "#64748b" }, // Slate
];

const formatCurrency = (val) => {
  return `₹${Number(val || 0).toLocaleString("en-IN")}`;
};

export default function OrgDealValueCard({
  organizations = [],
  totalDealValue = 0,
  searchQuery = "",
  loading = false,
}) {
  const [viewMode, setViewMode] = useState("funnel"); // 'funnel' | 'bars'
  const [limitMode, setLimitMode] = useState("5"); // '5' | '10' | 'all'
  const { showTooltip, moveTooltip, hideTooltip } = useDashboardTooltip();

  const filteredOrgs = useMemo(() => {
    let list = [...organizations];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter((o) => (o.organization || "").toLowerCase().includes(q));
    }
    // Sort by deal price descending
    list.sort((a, b) => (b.deal_price ?? b.price ?? 0) - (a.deal_price ?? a.price ?? 0));

    if (limitMode === "5") return list.slice(0, 5);
    if (limitMode === "10") return list.slice(0, 10);
    return list;
  }, [organizations, searchQuery, limitMode]);

  const maxVal = useMemo(() => {
    if (filteredOrgs.length === 0) return 1;
    return Math.max(...filteredOrgs.map((o) => o.deal_price ?? o.price ?? 0), 1);
  }, [filteredOrgs]);

  const isScrollable = filteredOrgs.length > 5;
  const rowHeight = 36;
  const minWidth = 18;
  const getWidth = (val) => minWidth + (100 - minWidth) * (val / maxVal);

  return (
    <div className="card c7" data-testid="organization-table-card">
      <div className="head">
        <h2>
          <LuFilter style={{ color: "var(--primary)" }} />
          <span>Deal Value by Organization</span>
        </h2>

        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
          <div className="seg" id="viewSeg">
            <button
              type="button"
              className={viewMode === "funnel" ? "on" : ""}
              onClick={() => setViewMode("funnel")}
              title="Funnel View"
            >
              Funnel
            </button>
            <button
              type="button"
              className={viewMode === "bars" ? "on" : ""}
              onClick={() => setViewMode("bars")}
              title="Bar View"
            >
              Bars
            </button>
          </div>

          <div className="seg" id="orgSeg">
            <button
              type="button"
              className={limitMode === "5" ? "on" : ""}
              onClick={() => setLimitMode("5")}
            >
              Top 5
            </button>
            <button
              type="button"
              className={limitMode === "10" ? "on" : ""}
              onClick={() => setLimitMode("10")}
            >
              Top 10
            </button>
            <button
              type="button"
              className={limitMode === "all" ? "on" : ""}
              onClick={() => setLimitMode("all")}
            >
              All
            </button>
          </div>
        </div>
      </div>

      {loading && filteredOrgs.length === 0 ? (
        <div style={{ display: "flex", flexDirection: "column", gap: "10px", padding: "10px 0" }}>
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={`org-skel-${i}`} className="skeleton-box" style={{ height: "36px", width: "100%" }} />
          ))}
        </div>
      ) : filteredOrgs.length === 0 ? (
        <div className="empty-state-card" data-testid="organization-empty-state">
          No organization data available for the selected date range.
        </div>
      ) : viewMode === "funnel" ? (
        <div
          className={`funnel-list ${isScrollable ? "scrollable-funnel" : ""}`}
          id="orgBars"
          data-testid="org-funnel-view"
        >
          {filteredOrgs.map((org, idx) => {
            const val = org.deal_price ?? org.price ?? 0;
            const wTop = getWidth(val);
            const nextVal =
              idx + 1 < filteredOrgs.length
                ? filteredOrgs[idx + 1].deal_price ?? filteredOrgs[idx + 1].price ?? 0
                : val * 0.78;
            let wBot = getWidth(nextVal);
            if (wBot > wTop) wBot = wTop;

            const padTop = ((100 - wTop) / 2).toFixed(2);
            const padBot = ((100 - wBot) / 2).toFixed(2);
            const grad = FUNNEL_GRADIENTS[idx % FUNNEL_GRADIENTS.length];
            const pct = totalDealValue > 0 ? ((val / totalDealValue) * 100).toFixed(1) : 0;
            const showTag = wTop >= 22;

            const tipContent = (
              <div>
                <div><b>Deal Value:</b> {formatCurrency(val)}</div>
                <div><b>Share:</b> {pct}% of total CRM deal value</div>
                <div><b>Leads:</b> {org.leads || 0}</div>
                {org.deals ? <div><b>Deals:</b> {org.deals}</div> : null}
              </div>
            );

            return (
              <div
                className="funnel-row"
                key={idx}
                onMouseEnter={(e) => showTooltip(e, org.organization, tipContent)}
                onMouseMove={moveTooltip}
                onMouseLeave={hideTooltip}
                data-testid={`funnel-row-${idx}`}
              >
                <div className="funnel-rank">{idx + 1}</div>
                <div className="funnel-name" title={org.organization}>
                  {org.organization}
                </div>
                <div className="funnel-track" style={{ height: `${rowHeight}px` }}>
                  <div
                    className="funnel-shape"
                    style={{
                      clipPath: `polygon(${padTop}% 0%, ${100 - padTop}% 0%, ${100 - padBot}% 100%, ${padBot}% 100%)`,
                      background: `linear-gradient(90deg, ${grad.start}, ${grad.end})`,
                    }}
                  >
                    {showTag && <span className="funnel-pct-tag">{pct}%</span>}
                  </div>
                </div>
                <div className="funnel-val">{formatCompact(val)}</div>
              </div>
            );
          })}
        </div>
      ) : (
        /* Bars View */
        <div
          className={`bars ${isScrollable ? "scrollable-funnel" : ""}`}
          id="orgBars"
          data-testid="org-bars-view"
        >
          {filteredOrgs.map((org, idx) => {
            const val = org.deal_price ?? org.price ?? 0;
            const widthPct = maxVal > 0 ? (val / maxVal) * 100 : 0;
            const pct = totalDealValue > 0 ? ((val / totalDealValue) * 100).toFixed(1) : 0;

            const tipContent = (
              <div>
                <div><b>Deal Value:</b> {formatCurrency(val)}</div>
                <div><b>Share:</b> {pct}% of total</div>
                <div><b>Leads:</b> {org.leads || 0}</div>
              </div>
            );

            return (
              <div
                className="bar-row"
                key={idx}
                onMouseEnter={(e) => showTooltip(e, org.organization, tipContent)}
                onMouseMove={moveTooltip}
                onMouseLeave={hideTooltip}
                data-testid={`bar-row-${idx}`}
              >
                <div className="bar-rank">{idx + 1}</div>
                <div className="bar-name" title={org.organization}>
                  {org.organization}
                </div>
                <div className="bar-track">
                  <div className="bar-fill" style={{ width: `${Math.max(2, widthPct)}%` }} />
                </div>
                <div className="bar-val">{formatCompact(val)}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
